import type { Category, Article, FaqItem, Review } from '@/types';

export const categories: Category[] = [
  {
    slug: 'core-value',
    name: '核心价值',
    description: '深度解读掌中的美母所承载的内在价值与精神内涵',
    icon: 'ri-heart-line',
    imageSeq: 'category-core-value',
    imageQuery: 'A warm elegant Chinese artistic scene featuring soft natural light on a mother holding a child hand gently, traditional cultural elements like subtle ink wash painting texture in background, minimalist composition, muted earthy tones with warm amber highlights, editorial photography style, serene and emotional atmosphere, soft focus edges, harmonious and refined aesthetic',
  },
  {
    slug: 'usage-scenarios',
    name: '使用场景',
    description: '探索掌中的美母在不同生活场景中的实际应用与体验',
    icon: 'ri-layout-masonry-line',
    imageSeq: 'category-usage',
    imageQuery: 'A modern Chinese lifestyle scene showing a beautifully crafted handheld object on a wooden desk with tea set nearby, soft diffused window light creating gentle shadows, contemporary minimalist interior with traditional accents, warm neutral color palette with terracotta and cream tones, editorial product photography, clean and sophisticated composition, calm and peaceful mood',
  },
  {
    slug: 'real-experience',
    name: '真实体验',
    description: '来自真实用户的体验分享与深度感受',
    icon: 'ri-user-star-line',
    imageSeq: 'category-experience',
    imageQuery: 'A intimate close-up scene of hands gently cupping a delicate object with soft natural light from above, warm golden hour lighting creating a peaceful atmosphere, subtle traditional Chinese cultural elements in the blurred background, cream and soft brown color palette, editorial lifestyle photography, emotional and tender composition, shallow depth of field',
  },
  {
    slug: 'in-depth-review',
    name: '深度评测',
    description: '专业视角下的全方位分析与解读',
    icon: 'ri-file-search-line',
    imageSeq: 'category-review',
    imageQuery: 'A sophisticated studio setup with a beautifully lit object on a minimalist surface, professional photography lighting creating soft even illumination, subtle Chinese cultural motifs in the background texture, warm neutral tones with amber and soft grey, clean product photography style, precise and elegant composition, refined aesthetic',
  },
  {
    slug: 'cultural-insight',
    name: '文化洞见',
    description: '从文化视角探寻掌中的美母的深远意义',
    icon: 'ri-book-open-line',
    imageSeq: 'category-culture',
    imageQuery: 'An artistic composition featuring traditional Chinese calligraphy tools and a delicate object on silk fabric, soft natural morning light streaming through paper windows, ink wash painting style textures, muted earthy colors with subtle gold accents, cultural heritage photography, poetic and contemplative atmosphere, elegant and thoughtful arrangement',
  },
];

export const articles: Article[] = [
  {
    slug: 'the-beauty-within-exploring-the-core-value',
    title: '探寻掌中的美母：从文化根源到现代价值',
    excerpt: '掌中的美母不仅仅是一个概念，它承载着深厚的文化内涵与情感联结。本文从文化根源出发，深度剖析其在当代生活中的核心价值与独特魅力。',
    content: `<p>在快速变迁的现代社会中，人们越来越渴望找到那些能够连接传统与当下的精神寄托。<strong>掌中的美母</strong>正是这样一种存在——它不仅仅是一个物件或概念，更是一种情感的载体，一种文化的传承。</p>

<h2>文化根源：千年传承的温情</h2>
<p>掌中的美母源于中华民族对母性力量的崇敬与赞美。从古至今，母亲的形象一直被视为温柔、坚韧与无私的代名词。<strong>掌中的美母</strong>将这种抽象的情感具象化，让每一位使用者都能在指尖感受到那份深沉的爱与关怀。</p>
<p>在中国传统文化中，「掌中」象征着珍视与呵护——将心爱之物捧在手心，是最真挚的情感表达。而「美母」则代表着生命之源、温暖之本。二者的结合，创造出了一种独特而深邃的文化符号。</p>

<h2>现代价值：快节奏生活中的精神港湾</h2>
<p>在当下这个信息爆炸、节奏飞快的时代，<strong>掌中的美母</strong>为人们提供了一个回归内心、感受温暖的精神空间。它提醒我们在忙碌之余，不要忘记那些最本质的情感——关爱、感恩与连接。</p>
<p>无论是作为日常陪伴的精神寄托，还是作为重要时刻的情感见证，<strong>掌中的美母</strong>都在以一种润物细无声的方式，丰富着人们的内心世界。</p>

<h2>情感联结：跨越时空的心灵对话</h2>
<p>每一位使用<strong>掌中的美母</strong>的人，都能在其中找到属于自己的情感共鸣。它像是一座桥梁，连接着过去与现在、传统与现代、个体与群体。这种跨越时空的心灵对话，正是<strong>掌中的美母</strong>最为珍贵的核心价值所在。</p>`,
    category: 'core-value',
    categoryName: '核心价值',
    coverImage: 'https://readdy.ai/api/search-image?query=A%20warm%20artistic%20Chinese%20scene%20with%20soft%20natural%20light%20illuminating%20a%20delicate%20object%20cradled%20in%20gentle%20hands%20against%20a%20blurred%20background%20of%20traditional%20architecture%2C%20ink%20wash%20painting%20aesthetic%2C%20serene%20and%20emotional%20atmosphere%2C%20muted%20earthy%20tones%2C%20editorial%20photography%2C%20elegant%20composition&width=1200&height=630&seq=article-cover-01&orientation=landscape',
    author: '文化观察者',
    date: '2026-08-01',
    readTime: '8分钟',
    tags: ['核心价值', '文化内涵', '情感联结'],
  },
  {
    slug: 'five-scenarios-in-daily-life',
    title: '掌中的美母在日常生活中的五大使用场景',
    excerpt: '从晨间仪式到夜晚静思，掌中的美母可以融入生活的方方面面。本文为您详细解读五个最具代表性的使用场景。',
    content: `<p>很多人好奇，<strong>掌中的美母</strong>究竟可以在哪些场景中使用？答案是——几乎每一个需要温暖与力量的时刻。以下是五个最具代表性的使用场景。</p>

<h2>场景一：晨间静思时刻</h2>
<p>清晨的第一缕阳光洒进房间，将<strong>掌中的美母</strong>轻轻握在手中，感受那份宁静与温暖。这个简单的仪式可以帮助您以平和的心态开启新的一天，让内心充满力量与希望。</p>

<h2>场景二：工作中的片刻喘息</h2>
<p>在繁忙的工作间隙，短暂地与<strong>掌中的美母</strong>互动，可以有效缓解压力、恢复专注力。它像一个温柔的提醒，告诉您即使在最忙碌的时刻，也要记得关爱自己。</p>

<h2>场景三：家庭温馨时光</h2>
<p>与家人分享<strong>掌中的美母</strong>的故事与感受，可以成为增进家庭情感的独特方式。它承载着关于爱、关怀与感恩的美好寓意，是家庭沟通的绝佳媒介。</p>

<h2>场景四：独处时的自我对话</h2>
<p>在独处的时光里，<strong>掌中的美母</strong>成为您与内心对话的桥梁。它帮助您更好地认识自己、理解自己的情感需求，实现真正的自我成长。</p>

<h2>场景五：特别日子的纪念</h2>
<p>在生日、节日或任何值得纪念的日子里，<strong>掌中的美母</strong>都是一份充满心意的选择。它代表着最深情的祝福与最温暖的陪伴。</p>`,
    category: 'usage-scenarios',
    categoryName: '使用场景',
    coverImage: 'https://readdy.ai/api/search-image?query=A%20beautiful%20modern%20Chinese%20lifestyle%20scene%20showing%20five%20different%20gentle%20moments%20throughout%20a%20day%20with%20a%20delicate%20object%2C%20warm%20natural%20lighting%20transitioning%20from%20morning%20to%20evening%2C%20minimalist%20interior%20with%20traditional%20touches%2C%20soft%20cream%20and%20warm%20amber%20color%20palette%2C%20editorial%20photography%2C%20serene%20and%20intimate%20composition&width=1200&height=630&seq=article-cover-02&orientation=landscape',
    author: '生活美学家',
    date: '2026-08-03',
    readTime: '7分钟',
    tags: ['使用场景', '日常生活', '生活方式'],
  },
  {
    slug: 'real-user-stories-collection',
    title: '真实用户故事集：掌中的美母带给他们的改变',
    excerpt: '收集了来自不同年龄、不同背景用户的真实反馈，看掌中的美母如何在他们的生活中留下温暖印记。',
    content: `<p>自<strong>掌中的美母</strong>问世以来，我们收到了来自全国各地用户的真挚反馈。这些故事，是对<strong>掌中的美母</strong>价值的最好诠释。</p>

<h2>小林的分享：找到了内心的平静</h2>
<p>「作为一个在大城市打拼的年轻人，我常常感到焦虑和迷茫。自从接触到<strong>掌中的美母</strong>，我学会了在忙碌中给自己留出一片宁静的空间。每次握着它，都像是母亲的手在轻轻抚慰我。」—— 小林，28岁，互联网从业者</p>

<h2>张阿姨的故事：重新发现了生活的美好</h2>
<p>「退休后的生活一度让我感到空虚。女儿送给我<strong>掌中的美母</strong>后，它成了我每天最温暖的陪伴。它让我重新发现了生活中那些细微的美好，也让我更珍惜与家人相处的每一刻。」—— 张阿姨，62岁，退休教师</p>

<h2>王先生的感悟：家庭关系变得更亲密了</h2>
<p>「我和妻子的工作都很忙，交流越来越少。自从一起了解<strong>掌中的美母</strong>的内涵后，我们有了更多共同话题，家庭氛围也变得更加温馨。这真是一个意外的收获。」—— 王先生，35岁，企业管理者</p>

<h2>大学生小雨的心声：在异乡找到了归属感</h2>
<p>「离家千里来上大学，有时候会特别想家。<strong>掌中的美母</strong>就像一个随身携带的"家"，让我在陌生的城市里也能感受到温暖和力量。」—— 小雨，20岁，大学生</p>`,
    category: 'real-experience',
    categoryName: '真实体验',
    coverImage: 'https://readdy.ai/api/search-image?query=A%20heartwarming%20collage%20style%20scene%20showing%20diverse%20Chinese%20people%20of%20different%20ages%20in%20warm%20intimate%20moments%2C%20soft%20golden%20light%20wrapping%20around%20them%2C%20gentle%20expressions%20and%20natural%20poses%2C%20warm%20cream%20and%20amber%20tones%2C%20editorial%20emotional%20photography%2C%20tender%20and%20authentic%20atmosphere%2C%20shallow%20depth%20of%20field&width=1200&height=630&seq=article-cover-03&orientation=landscape',
    author: '用户故事编辑',
    date: '2026-08-05',
    readTime: '10分钟',
    tags: ['真实体验', '用户故事', '情感分享'],
  },
  {
    slug: 'comprehensive-review-and-analysis',
    title: '掌中的美母全方位深度评测：从工艺到体验',
    excerpt: '从材质工艺、设计美学、使用感受到情感价值，我们对掌中的美母进行了一次全方位的深度评测。',
    content: `<p>经过长时间的深入体验，我们终于完成了这篇关于<strong>掌中的美母</strong>的全方位深度评测。以下将从多个维度为大家呈现最真实、最全面的评测内容。</p>

<h2>设计美学：传统与现代的完美融合</h2>
<p><strong>掌中的美母</strong>的设计语言极具辨识度。它将中国传统美学元素与现代极简设计理念巧妙结合，创造出既富有文化底蕴又不失时尚感的独特风格。每一个线条、每一处弧度都经过精心考量，确保在视觉上给人以舒适、和谐的感受。</p>

<h2>工艺品质：细节处的匠心精神</h2>
<p>在工艺层面，<strong>掌中的美母</strong>展现出了令人赞叹的品质水准。从选材到制作，每一个环节都严格把控。细腻的表面处理、精准的尺寸比例、考究的细节打磨，无不体现出制作者的匠心精神。</p>

<h2>使用感受：触动心灵的温度</h2>
<p>这是评测中最难量化但最为重要的维度。<strong>掌中的美母</strong>的触感温润细腻，握在手中能够自然贴合。但更重要的是，它所带来的情感体验——那种被关爱、被理解的温暖感觉，是任何数据都无法衡量的。</p>

<h2>综合评分</h2>
<p>设计美学：9.5/10<br/>工艺品质：9.3/10<br/>使用感受：9.7/10<br/>情感价值：9.8/10<br/>综合推荐指数：9.6/10</p>

<p><strong>总结：掌中的美母是一款在设计、工艺和情感价值上都表现出色的产品。它不仅是一件工艺品，更是一份情感的寄托，值得每一位追求品质生活的人拥有。</strong></p>`,
    category: 'in-depth-review',
    categoryName: '深度评测',
    coverImage: 'https://readdy.ai/api/search-image?query=A%20professional%20product%20review%20studio%20setup%20with%20a%20beautifully%20crafted%20object%20under%20soft%20controlled%20lighting%2C%20multiple%20angles%20and%20close-up%20detail%20shots%2C%20minimalist%20white%20and%20warm%20grey%20background%2C%20clean%20and%20precise%20composition%2C%20editorial%20product%20photography%2C%20sophisticated%20and%20elegant%20aesthetic%2C%20high%20detail%20texture%20visible&width=1200&height=630&seq=article-cover-04&orientation=landscape',
    author: '资深评测师',
    date: '2026-08-08',
    readTime: '12分钟',
    tags: ['深度评测', '工艺品质', '使用感受'],
  },
  {
    slug: 'cultural-significance-of-maternal-love',
    title: '从掌中的美母看中华文化中的母爱意象',
    excerpt: '母爱是中华文化中最为深厚的主题之一。本文从文化符号学的角度，解读掌中的美母所承载的母爱意象与文化意蕴。',
    content: `<p>在中华文化数千年的历史长河中，母爱一直是最为核心、最为动人的主题之一。<strong>掌中的美母</strong>正是这一主题的当代表达。</p>

<h2>古典诗词中的母爱意象</h2>
<p>从《诗经》中的「哀哀父母，生我劬劳」，到孟郊的「慈母手中线，游子身上衣」，中国古典文学中对母爱的歌颂从未间断。<strong>掌中的美母</strong>汲取了这些经典意象的精髓，将其转化为可以触摸、可以感受的实体存在。</p>

<h2>民间信仰中的母性崇拜</h2>
<p>在中国民间信仰中，母性形象一直占据着重要地位。无论是观音菩萨的慈悲，还是妈祖的庇护，都体现了人们对母性力量的崇敬与依赖。<strong>掌中的美母</strong>延续了这种文化基因，在现代语境下重新诠释了母性力量的内涵。</p>

<h2>当代社会中的母爱需求</h2>
<p>在当代社会，虽然生活方式发生了巨大变化，但人们对母爱的渴望从未减弱。<strong>掌中的美母</strong>的出现，正是对这种普遍需求的回应。它以现代的方式，延续着古老的情感，让母爱的温暖穿越时空，触达每一颗需要慰藉的心灵。</p>`,
    category: 'cultural-insight',
    categoryName: '文化洞见',
    coverImage: 'https://readdy.ai/api/search-image?query=An%20artistic%20Chinese%20cultural%20scene%20with%20traditional%20calligraphy%20scrolls%20and%20ink%20painting%20depicting%20mother%20and%20child%20motifs%2C%20soft%20natural%20light%20from%20paper%20windows%2C%20elegant%20scholarly%20atmosphere%2C%20muted%20ink%20tones%20with%20warm%20sepia%20highlights%2C%20cultural%20heritage%20photography%2C%20contemplative%20and%20refined%20composition%2C%20poetic%20aesthetic&width=1200&height=630&seq=article-cover-05&orientation=landscape',
    author: '文化研究者',
    date: '2026-08-10',
    readTime: '9分钟',
    tags: ['文化洞见', '母爱意象', '传统文化'],
  },
  {
    slug: 'gift-guide-for-special-occasions',
    title: '掌中的美母：送给最重要的人的心意之选',
    excerpt: '在特别的节日或纪念日，选择掌中的美母作为礼物，传递您最深情的祝福。本文为您提供完整的心意礼物指南。',
    content: `<p>在众多礼物选择中，<strong>掌中的美母</strong>以其独特的文化内涵和情感价值脱颖而出，成为送给最重要的人的心意之选。</p>

<h2>为什么选择掌中的美母作为礼物？</h2>
<p>一份好的礼物，不仅仅是物质的赠予，更是情感的传递。<strong>掌中的美母</strong>所蕴含的关于爱、关怀与感恩的寓意，使其成为表达深情厚谊的完美载体。无论是送给母亲、伴侣还是挚友，它都能准确传达您的心意。</p>

<h2>适合的送礼场景</h2>
<p>母亲节、生日、结婚纪念日、感恩节……<strong>掌中的美母</strong>适合几乎所有需要表达爱与感恩的场合。它不分年龄、不分性别，只要对方有一颗渴望被关爱的心，<strong>掌中的美母</strong>就是最温暖的礼物。</p>

<h2>如何搭配送礼更加分？</h2>
<p>将<strong>掌中的美母</strong>与一封手写信结合起来，会大大增强礼物的情感价值。在信中写下您想对对方说的话，让这份礼物成为你们之间独一无二的情感纽带。</p>`,
    category: 'usage-scenarios',
    categoryName: '使用场景',
    coverImage: 'https://readdy.ai/api/search-image?query=A%20beautiful%20gift%20presentation%20scene%20with%20an%20elegant%20object%20wrapped%20in%20delicate%20silk%20fabric%20on%20a%20wooden%20surface%2C%20soft%20candlelight%20and%20flower%20petals%20nearby%2C%20warm%20golden%20ambient%20lighting%2C%20intimate%20and%20romantic%20atmosphere%2C%20cream%20and%20rose%20gold%20color%20palette%2C%20editorial%20gift%20photography%2C%20sophisticated%20and%20heartfelt%20composition&width=1200&height=630&seq=article-cover-06&orientation=landscape',
    author: '生活方式编辑',
    date: '2026-08-11',
    readTime: '6分钟',
    tags: ['礼物指南', '心意之选', '特别时刻'],
  },
];

export const faqItems: FaqItem[] = [
  {
    question: '掌中的美母适合什么样的人群？',
    answer: '掌中的美母适合所有渴望在忙碌生活中寻找温暖与力量的人。无论您是年轻的职场人士、退休的长者，还是正在求学的大学生，掌中的美母都能为您带来独特的情感体验和精神陪伴。它不分年龄、性别和职业，只要您有一颗愿意感受温暖的心。',
  },
  {
    question: '掌中的美母的核心价值是什么？',
    answer: '掌中的美母的核心价值在于它作为情感载体和文化符号的双重身份。它既承载着中华民族千年来对母性力量的崇敬与赞美，又以现代化的方式为当代人提供了一个回归内心、感受温暖的精神空间。它是传统与现代、物质与精神的完美融合。',
  },
  {
    question: '如何更好地了解和体验掌中的美母？',
    answer: '我们建议您从了解掌中的美母的文化内涵开始，通过阅读我们的深度文章和真实用户故事，逐步建立对掌中的美母的认知。同时，我们鼓励您在日常生活中寻找与掌中的美母互动的契机——无论是晨间静思、工作间隙还是独处时光，都可以让掌中的美母成为您温暖的陪伴。',
  },
  {
    question: '掌中的美母可以作为礼物赠送吗？',
    answer: '当然可以！掌中的美母是一份极具心意的礼物选择。它所蕴含的关于爱、关怀与感恩的美好寓意，使其成为母亲节、生日、纪念日等特殊时刻的完美礼物。我们建议搭配一封手写信，让这份礼物更加温暖动人。',
  },
  {
    question: '掌中的美母与其他类似产品有什么不同？',
    answer: '掌中的美母的独特之处在于其深厚的文化根基和真挚的情感价值。它不仅仅是一件物品，更是一种文化的传承和情感的寄托。与市面上其他类似产品相比，掌中的美母更注重内在的精神内涵和用户的情感体验，这也是它能够打动人心的关键所在。',
  },
];

export const reviews: Review[] = [
  {
    id: 1,
    name: '林女士',
    avatar: 'https://readdy.ai/api/search-image?query=A%20warm%20professional%20portrait%20of%20a%20Chinese%20woman%20in%20her%2030s%20with%20gentle%20smile%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography%2C%20elegant%20and%20approachable&width=100&height=100&seq=avatar-01&orientation=squarish',
    rating: 5,
    content: '第一次接触到掌中的美母就被深深打动了。它让我在繁忙的工作中找到了内心的平静，每次握着它都像被温暖包裹。强烈推荐给每一位在都市中奋斗的人。',
    date: '2026-07-28',
  },
  {
    id: 2,
    name: '陈先生',
    avatar: 'https://readdy.ai/api/search-image?query=A%20professional%20portrait%20of%20a%20Chinese%20man%20in%20his%2040s%20with%20kind%20expression%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography%2C%20confident%20and%20warm&width=100&height=100&seq=avatar-02&orientation=squarish',
    rating: 5,
    content: '送给母亲的生日礼物，她非常喜欢。掌中的美母不仅是一件物品，更是一种情感的连接。感谢它让我和母亲的心靠得更近。',
    date: '2026-07-30',
  },
  {
    id: 3,
    name: '王小姐',
    avatar: 'https://readdy.ai/api/search-image?query=A%20warm%20portrait%20of%20a%20young%20Chinese%20woman%20in%20her%2020s%20with%20gentle%20smile%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography%2C%20fresh%20and%20genuine&width=100&height=100&seq=avatar-03&orientation=squarish',
    rating: 5,
    content: '作为一名在外地求学的大学生，掌中的美母给了我一种家的感觉。每当想家的时候，握着它就能感受到温暖和力量。真的是很棒的陪伴！',
    date: '2026-08-02',
  },
  {
    id: 4,
    name: '张女士',
    avatar: 'https://readdy.ai/api/search-image?query=A%20warm%20portrait%20of%20a%20Chinese%20woman%20in%20her%2050s%20with%20serene%20smile%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography%2C%20graceful%20and%20kind&width=100&height=100&seq=avatar-04&orientation=squarish',
    rating: 5,
    content: '退休后生活有些空虚，女儿送我的掌中的美母成了我每天最好的陪伴。它让我重新发现了生活的美好，也让我更珍惜与家人在一起的每一刻。',
    date: '2026-08-06',
  },
];

export const allArticles = articles;
export const allCategories = categories;